import React, { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import type { CredentialResponse } from '@react-oauth/google';
import axios from 'axios'; // Import axios
import {
  Paper,
  Box,
  Alert,
  AlertTitle,
} from '@mui/material';
import { BACKEND_AUTH_URL } from '../config'; // Import centralized BACKEND_AUTH_URL
import type { AppUser } from '../types'; // Import shared AppUser type

// Helper to decode JWT (very basic, for demo purposes only)
// In a real app, send the token to backend for verification and to get user info.
function decodeJwt(token: string): any | null {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error("Failed to decode JWT:", e);
    return null;
  }
}

// const BACKEND_AUTH_URL = 'https://myhome.cfc.io/api/auth/google/callback'; // Removed, now imported

interface LoginButtonProps {
  onLoginSuccess: (userData: AppUser, isNewUser: boolean) => void;
  onLoginError: (errorMessage?: string) => void;
  activeReferralCode?: string | null;
}

const LoginButton: React.FC<LoginButtonProps> = ({ onLoginSuccess, onLoginError, activeReferralCode }) => {
  const [componentLoginError, setComponentLoginError] = useState<string | null>(null);
  // Removed states that are now managed by App.tsx: backendUser, isNewUser, welcomeMessage
  // const [decodedTokenData, setDecodedTokenData] = useState<any | null>(null); // Keep for local debug if needed

  const handleSuccess = async (credentialResponse: CredentialResponse) => {
    console.log('Frontend Google Login Success:', credentialResponse);
    setComponentLoginError(null);

    if (credentialResponse.credential) {
      const currentIdToken = credentialResponse.credential;
      // const decoded = decodeJwt(currentIdToken); // You can still decode for local console log if you want
      // console.log("Frontend Decoded JWT (LoginButton):", decoded);

      try {
        console.log("Sending ID token to backend...");
        // Include activeReferralCode in the payload if it exists
        const payloadToBackend: { token: string; referralCode?: string | null } = {
          token: currentIdToken,
        };
        if (activeReferralCode) {
          payloadToBackend.referralCode = activeReferralCode;
          console.log(`[LoginButton] Including referral code in login request: ${activeReferralCode}`);
        }

        const response = await axios.post(BACKEND_AUTH_URL, payloadToBackend);
        console.log('Backend verification response:', response.data);
        
        // Call the callback passed from App.tsx
        onLoginSuccess(response.data.user as AppUser, response.data.isNewUser as boolean);
        // alert might be redundant if App.tsx handles welcome message

      } catch (error: any) {
        console.error('Backend verification failed:', error);
        let backendErrorMessage = 'Backend verification failed.';
        if (axios.isAxiosError(error) && error.response) {
          backendErrorMessage = `Backend Error ${error.response.status}: ${error.response.data?.message || 'Service error'}`;
        } else if (error.message) {
          backendErrorMessage = error.message;
        }
        setComponentLoginError(backendErrorMessage);
        onLoginError(backendErrorMessage);
      }
    } else {
      const noTokenMsg = 'No credential token received from Google.';
      console.error(noTokenMsg);
      setComponentLoginError(noTokenMsg);
      onLoginError(noTokenMsg);
    }
  };

  const handleError = () => {
    console.error('Google Login Failed on Frontend (in LoginButton component)');
    const errMsg = 'Google login attempt failed. Please try again.';
    setComponentLoginError(errMsg);
    onLoginError(errMsg);
  };

  return (
    <Paper elevation={3} sx={{ padding: 2, marginTop: 2 }}>
      {/* Typography for "Google Login" can be removed if App.tsx handles the "Login to access:" text */}
      {/* <Typography variant="h6" gutterBottom>Google Login</Typography> */}
      <Box sx={{ marginTop: 1, display: 'flex', justifyContent: 'center', width: '100%' }}>
        <GoogleLogin 
          onSuccess={handleSuccess} 
          onError={handleError} 
          useOneTap 
          containerProps={{ style: { width: '100%' } }}
          size="large"
        />
      </Box>
      
      {componentLoginError && (
        <Alert severity="error" sx={{ marginTop: 2 }}>
          <AlertTitle>Login Attempt Error</AlertTitle>
          {componentLoginError}
        </Alert>
      )}
      {/* Informational displays about token/user are now primarily handled in App.tsx */}
    </Paper>
  );
};

export default LoginButton; 